// src/lib/pharmacyFirst/pathways/aht/aht-wt-semaglutide-transfer.pathway.ts
//
// AHT Weight Management — Semaglutide (Wegovy®) Transfer Supply Pathway
// Source: AHT_Semaglutide_Wegovy_Clinical_Pathway.docx v1.4
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// For patients transferring from another provider already established on semaglutide.

import type { Pathway } from "../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q0_RED_FLAGS     = "sem-trans-q0-red-flags";
const Q0A_BMI_FLOOR    = "sem-trans-q0a-bmi-floor";
const Q1_F2F           = "sem-trans-q1-f2f";
const CONFIRM_CARD     = "sem-trans-confirmation-card";
const PGD_FRESH_CHECKS = "sem-trans-pgd-fresh-checks";
const Q3_INT_NTI       = "sem-trans-int1-narrow-ti";
const Q4_INT_OCP       = "sem-trans-int2-contraceptives";
const Q5_INT_GLP1      = "sem-trans-int3-glp1";
const Q6_CAUTIONS      = "sem-trans-cautions";
const Q7_TRANSFER_FLAG = "sem-trans-transfer-flag";
const Q8_DOSE          = "sem-trans-dose-select";
const Q9_ATTEST        = "sem-trans-attest";

const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtSemaglutideTransferPathway: Pathway = {
  id: "aht-wt-semaglutide-transfer",
  name: "Wegovy® (semaglutide) — Transfer Supply",
  population: "Adults transferring from another provider already established on semaglutide",
  pgdRefs: ["pgd-aht-wt-semaglutide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q0_RED_FLAGS,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 0 — Red flags gateway
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0_RED_FLAGS,
      type: "gateway_progression",
      triggersPayment: false,
      heading: "Check for red flags before proceeding",
      body:
        "Before proceeding with transfer supply, confirm the patient has none of the following:\n" +
        "• Severe or persistent abdominal pain (possible pancreatitis — refer urgently)\n" +
        "• Signs of allergic reaction or anaphylaxis — do not supply\n" +
        "• Pregnancy confirmed or suspected — discontinue\n" +
        "• New diagnosis of thyroid cancer, MEN2, or pancreatitis\n" +
        "If any red flag is present, do not proceed — refer to GP or A&E as appropriate.",
      next: Q0A_BMI_FLOOR,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 0a — BMI floor exclusion (transfer patients only)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0A_BMI_FLOOR,
      type: "checklist",
      text: "Does any of the following apply to this patient?",
      hint: "Select if present. Semaglutide (Wegovy®) must be discontinued if BMI falls below 22.5 kg/m² during treatment. If this applies, supply cannot proceed.",
      items: [
        {
          id: "sem-trans-bmi-floor",
          text: "BMI has fallen below 22.5 kg/m² since starting treatment",
        },
      ],
      includeNoneOption: true,
      noneOptionLabel: "None of the above — no exclusion criteria present",
      anySelected: { outcomeId: OUT_CANNOT_SUPPLY },
      noneSelected: Q1_F2F,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedFaceToFace"] === false,
          then: { outcomeId: OUT_NO_F2F },
        },
      ],
      default: "sem-trans-q-thyroid",
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2a–2g — Clinical screening (replaces confirmation card)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: "sem-trans-q-thyroid",
      type: "yes_no",
      text: "Does the patient have a personal or family history of thyroid cancer or MEN2?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-pancreatitis",
    },
    {
      id: "sem-trans-q-pancreatitis",
      type: "yes_no",
      text: "Does the patient have a history of pancreatitis (acute or chronic)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-t1dm",
    },
    {
      id: "sem-trans-q-t1dm",
      type: "yes_no",
      text: "Does the patient have Type 1 or Type 3c diabetes mellitus?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-renal",
    },
    {
      id: "sem-trans-q-renal",
      type: "yes_no",
      text: "Does the patient have severe or end-stage renal disease (CrCl <15 mL/min)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-hepatic",
    },
    {
      id: "sem-trans-q-hepatic",
      type: "yes_no",
      text: "Does the patient have severe hepatic impairment?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-gi",
    },
    {
      id: "sem-trans-q-gi",
      type: "yes_no",
      text: "Does the patient have severe or unstable gastrointestinal disease including gastroparesis?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-pregnancy",
    },
    {
      id: "sem-trans-q-pregnancy",
      type: "yes_no",
      text: "Is the patient pregnant, breastfeeding, or planning pregnancy?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "sem-trans-q-retinopathy",
    },
    {
      id: "sem-trans-q-retinopathy",
      type: "yes_no",
      text: "Does the patient have diabetic retinopathy or macular oedema?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2b — PGD inclusion / exclusion checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: PGD_FRESH_CHECKS,
      type: "pgd_exclusion_check",
      variant: "sequential",
      pgdIdOverride: "pgd-aht-wt-semaglutide",
      anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
      allCleared: Q3_INT_NTI,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3 — Interaction checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q3_INT_NTI,
      type: "yes_no",
      text: "Is the patient taking any oral medicines with a narrow therapeutic index?",
      hint: "Semaglutide delays gastric emptying — may affect absorption.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the monitoring and management plan for this interaction",
        acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
      },
      yes: Q4_INT_OCP,
      no: Q4_INT_OCP,
    },

    {
      id: Q4_INT_OCP,
      type: "yes_no",
      text: "Is the patient using oral contraceptives?",
      hint: "Add barrier method for 4 weeks after starting or any dose escalation.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the contraceptive counselling and management plan",
        acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
      },
      yes: Q5_INT_GLP1,
      no: Q5_INT_GLP1,
    },

    {
      id: Q5_INT_GLP1,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedConcurrentWeightAgent"] === true,
          then: { outcomeId: OUT_CANNOT_SUPPLY },
        },
      ],
      default: Q6_CAUTIONS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Cautions review
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q6_CAUTIONS,
      type: "flag",
      variant: "warn",
      message:
        "Review cautions before supply:\n" +
        "• GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk, especially in elderly\n" +
        "• Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops\n" +
        "• Pancreatitis suspected — stop immediately; do not restart if confirmed\n" +
        "• Monitor resting heart rate; report palpitations\n" +
        "• Report mood or behavioural changes\n" +
        "• Not a substitute for insulin\n" +
        "• Aspiration risk — advise of delayed gastric emptying before GA or deep sedation\n" +
        "• Animal studies show developmental toxicity — avoid in pregnancy",
      next: Q7_TRANSFER_FLAG,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Transfer notice
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q7_TRANSFER_FLAG,
      type: "flag",
      variant: "info",
      message:
        "Transfer from another provider — patient is already established on this medication.\n" +
        "Confirm the patient's current dose before supplying.\n" +
        "• Request evidence of previous supply (prescription, dispensing label, or transfer letter)\n" +
        "• Verify the dose matches the patient's stated current regimen\n" +
        "• Supply at the established dose — do not restart dose titration",
      next: Q8_DOSE,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Current dose selection
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q8_DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "sem-0_25mg", label: "0.25 mg SC once weekly" },
        { id: "sem-0_5mg",  label: "0.5 mg SC once weekly" },
        { id: "sem-1_0mg",  label: "1.0 mg SC once weekly" },
        { id: "sem-1_7mg",  label: "1.7 mg SC once weekly" },
        { id: "sem-2_4mg",  label: "2.4 mg SC once weekly" },
      ],
      routing: {
        "sem-0_25mg": Q9_ATTEST,
        "sem-0_5mg":  Q9_ATTEST,
        "sem-1_0mg":  Q9_ATTEST,
        "sem-1_7mg":  Q9_ATTEST,
        "sem-2_4mg":  Q9_ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 7 — Supply attestation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q9_ATTEST,
      type: "attestation",
      text: "Confirm transfer supply of Wegovy® (semaglutide)",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied 1 × Wegovy® pre-filled pen at the patient's confirmed established dose (4 doses = 28-day supply)\n" +
        "• Verified the dose against evidence of previous supply\n" +
        "• Checked pen integrity and cold chain before supply\n" +
        "• Confirmed storage: 2–8°C before first use; after first use ≤30°C or refrigerated for up to 6 weeks\n" +
        "• Counselled the patient on injection technique and site rotation\n" +
        "• Advised the patient to inform their GP of the provider transfer\n" +
        "• Provided patient information leaflet\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    [OUT_NO_F2F]: {
      id: OUT_NO_F2F,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — face-to-face consultation required",
      reason: "Supply under this PGD requires the patient to attend in person at AHT registered premises.",
      safetyNetting: ["Ask the patient to book an in-person appointment to proceed with supply."],
    },
    [OUT_CANNOT_SUPPLY]: {
      id: OUT_CANNOT_SUPPLY,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — Wegovy® (semaglutide)",
      reason: "An inclusion criterion was not met or an exclusion criterion was triggered. Supply cannot proceed under this PGD.",
      safetyNetting: [
        "Discuss the reason supply cannot proceed with the patient.",
        "Consider referral to prescriber or GP if clinically appropriate.",
      ],
    },
    [OUT_SUPPLY]: {
      id: OUT_SUPPLY,
      type: "outcome",
      outcomeType: "supply",
      title: "Transfer supply complete — Wegovy® (semaglutide)",
      reason: "All inclusion criteria met, no exclusion criteria triggered, and transfer supply attestation confirmed.",
      drugRef: "semaglutide",
      safetyNetting: [
        "Return in 4 weeks for monthly monitoring review.",
        "Missed dose: if ≤5 days late, give now and resume usual schedule. If >5 days late, skip and resume next scheduled dose.",
        "Contact the service immediately if any signs of allergic reaction, severe abdominal pain, or persistent vomiting.",
        "Store unused pens in the refrigerator (2–8°C). After first use, may store at ≤30°C or refrigerated for up to 6 weeks.",
      ],
    },
  },
};
